import React, { type Ref } from 'react';
import { differenceInDays } from 'date-fns';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { xcss, Pressable } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	BANNER,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import {
	useJsmGracePeriodAnnouncementDisplayStateResource,
	useJsmGracePeriodAnnouncementGpEndDateResource,
} from '@atlassian/jira-router-resources-jsm-grace-period-announcements/src/services/index.tsx';
import { FALLBACK_GP_END_DATE } from '@atlassian/jira-router-resources-jsm-grace-period-announcements/src/services/constants.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { premiumLandingPageEntryPoint } from '@atlassian/jira-servicedesk-premium-landing-page/entrypoint.tsx';
import { Trigger, usePanel } from '@atlassian/entry-point-panel';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { STANDARD_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useProductAndEdition } from '@atlassian/jira-edition-awareness-for-standard-to-premium/src/services/use-product-and-edition/index.tsx';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';
import { SERVICE_DESK } from '@atlassian/jira-shared-types/src/application-key.tsx';
import type { GpAwarenessPillProps } from './types.tsx';
import messages from './messages.tsx';

export const GpAwarenessPill = ({ analyticsOrigin }: GpAwarenessPillProps) => {
	const { formatMessage } = useIntl();
	const accountId = useAccountId();
	const isSiteAdmin = useIsSiteAdmin();
	const appEditions = useAppEditions();
	const { product } = useProductAndEdition();
	const {
		gpEndDate,
		gpEndDateDetails,
		loading: gpEndDateLoading,
	} = useJsmGracePeriodAnnouncementGpEndDateResource(accountId);
	const { displayState: bannerDisplayState, loading: bannerDisplayStateLoading } =
		useJsmGracePeriodAnnouncementDisplayStateResource(accountId);
	const { Panel, trigger } = usePanel();
	const productEntitlementDetails = useProductEntitlementDetails();
	const isCCP = productEntitlementDetails?.[SERVICE_DESK]?.billingSourceSystem === 'CCP';

	const daysLeft = differenceInDays(
		gpEndDate ? new Date(gpEndDate) : FALLBACK_GP_END_DATE,
		new Date(),
	);

	const isGPFinished = daysLeft <= 0;
	const isGPMonthlyBilling = gpEndDateDetails?.billingPeriod === 'MONTHLY';
	const announcementType = daysLeft > 30 ? 'T90' : 'T30';
	const fallbackDaysLeft = announcementType === 'T90' ? 90 : 30;

	const handleOnClick = (
		_: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if (analyticsOrigin) {
			fireUIAnalytics(analyticsEvent, 'jsmGracePeriodPill', {
				origin: analyticsOrigin,
			});
		}
	};

	const bannerIsVisible =
		(announcementType === 'T90' && !bannerDisplayState?.hideT90Banner) ||
		(announcementType === 'T30' && !bannerDisplayState?.hideT30Banner);

	if (
		!isSiteAdmin ||
		appEditions.serviceDesk !== STANDARD_EDITION ||
		gpEndDateLoading ||
		bannerDisplayStateLoading ||
		bannerIsVisible ||
		product !== 'jira-service-management' ||
		isGPFinished ||
		!isCCP ||
		!isGPMonthlyBilling
	) {
		return;
	}

	return (
		<JSErrorBoundary
			id="jsm-grace-period-banner.ui.gp-awareness-pill"
			packageName="jsm-grace-period-awareness-pill"
			teamName="Sequoia"
			fallback="unmount"
		>
			<ContextualAnalyticsData
				sourceName="jsmGracePeriodPill"
				sourceType={BANNER}
				attributes={{ daysLeftInGP: daysLeft }}
			>
				<FireScreenAnalytics />
				<Trigger
					trigger={trigger}
					entryPoint={premiumLandingPageEntryPoint}
					entryPointProps={{
						origin: 'NAV_BAR',
						onClose: () => {
							trigger.setPanel(null);
						},
					}}
				>
					{({ ref }) => (
						<Pressable
							onClick={handleOnClick}
							xcss={[pressableStyles, announcementType === 'T30' && warningStyles]}
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							ref={ref as Ref<HTMLButtonElement>}
							testId="jsm-grace-period-banner.ui.gp-awareness-pill.button"
						>
							<WarningIcon label="" color={token('color.icon.warning')} />
							{formatMessage(messages.daysLeft, {
								days: gpEndDate ? daysLeft : fallbackDaysLeft,
								symbol: gpEndDate ? '' : '+',
							})}
						</Pressable>
					)}
				</Trigger>
			</ContextualAnalyticsData>
			<Panel />
		</JSErrorBoundary>
	);
};

const pressableStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	textDecoration: 'none !important',
	backgroundColor: 'color.background.neutral.subtle',
	display: 'inline-flex',
	border: `1px solid ${token('color.border.warning')}`,
	borderRadius: 'border.radius.100',
	color: 'color.text.warning',
	flexShrink: 0,
	height: '2rem',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	paddingBlock: 'space.075',
	paddingInlineEnd: 'space.150',
	paddingInlineStart: 'space.150',
	textAlign: 'center',
	transition: 'background 0.1s ease-out',
	alignItems: 'center',
	gap: 'space.075',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const warningStyles = xcss({
	backgroundColor: 'color.background.warning',

	':hover': {
		backgroundColor: 'color.background.warning.hovered',
	},
	':active': {
		backgroundColor: 'color.background.warning.pressed',
	},
});
